import Typography from "components/elements/Typography";
import classes from "./classes.module.scss";
import classNames from "classnames";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useState } from "react";
import { FormContext } from "../FormContext";

export type TextAreaRef = {
	clear: () => void;
};

type IProps = {
	name: string;
	label?: string;
	additionalInfo?: string;
	required?: boolean;
	placeholder?: string;
	defaultValue?: string | null;
	onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	className?: string;
	inputClassName?: string;
	disabled?: boolean;
};

export const TextArea = forwardRef<TextAreaRef, IProps>((props, ref) => {
	const [value, setValue] = useState(props.defaultValue || "");
	const context = useContext(FormContext);
	let errors = context?.getMessagesErrors(props.name);
	const hasErrors = errors?.length > 0;

	useImperativeHandle(ref, () => ({
		clear: () => setValue(props.defaultValue || ""),
	}));

	const onWheel = useCallback((event: React.WheelEvent<HTMLTextAreaElement>) => {
		(event.target as HTMLInputElement).blur();
	}, []);

	// No useCallBack here because the only dependency is props.onChange
	const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		const inputValue = event.target.value;
		let valueToSet = inputValue;

		setValue(valueToSet);
		event.target.value = valueToSet;
		props.onChange && props.onChange(event);
	};

	useEffect(() => {
		setValue(props.defaultValue || "");
	}, [props.defaultValue]);

	return (
		<div className={classNames(classes["root"], props.className)}>
			{props.label && (
				<label htmlFor={props.name} className={classes["label"]}>
					<Typography type="span" weight="regular" size="medium">
						{props.label}
						{props.required && "*"}
					</Typography>
				</label>
			)}
			<textarea
				id={props.name}
				placeholder={props.placeholder}
				rows={8}
				onWheel={onWheel}
				name={props.name}
				required={props.required}
				value={value}
				onChange={handleInputChange}
				className={classNames(props.inputClassName, classes["input"])}
				disabled={props.disabled}>
				{value}
			</textarea>
			{props.additionalInfo && (
				<Typography type="span" weight="regular" size="small" fontFamily="secondary" className={classes["additional-info"]}>
					{props.additionalInfo}
				</Typography>
			)}
			{hasErrors && (
				<div className={classes["errors-container"]}>
					{errors.map((message, i) => (
						<Typography type="p" size="small" weight="medium" key={i} color="error">
							{message}
						</Typography>
					))}
				</div>
			)}
		</div>
	);
});
